import {
  ClipboardCopyIcon,
  DownloadIcon,
  PlayIcon,
} from '@radix-ui/react-icons';
import { Badge, Flex, IconButton, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { getFireVideoID } from 'components/common/video/annotated-player';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-errors/common';
import { ACTIONS_KEY } from 'enums/tables';
import { IMenuAction } from 'interfaces/i-menus';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IFireEvent } from 'lib_ts/interfaces/i-session-event';
import { useState } from 'react';
import { MainService } from 'services/main.service';
import { StereoVideosService } from 'services/stereo-videos.service';

interface IProps {
  fires: IFireEvent[];
  newFireID: string;
  updatedFireID: string;
  onSelect: (fire: IFireEvent) => void;
}

const COLUMNS: ITypedTableColumn<IFireEvent>[] = [
  {
    label: 'Date',
    key: '_created',
    formatFn: (f) => <Text size="1">{formatDate(f._created)}</Text>,
  },
  {
    label: 'Pitch Name',
    key: 'pitchName',
    formatFn: (f) => (
      <Badge
        color={f.data.pitch_id ? RADIX.COLOR.ACCENT : RADIX.COLOR.SECONDARY}
      >
        {f.data.pitch_name || 'N/A'}
      </Badge>
    ),
  },
  {
    label: 'Mode',
    key: 'mode',
    formatFn: (f) => <Text>{f.data.training ? 'Training' : 'BP'}</Text>,
  },
  {
    label: 'Hit Result',
    key: 'hit_result',
    formatFn: (f) => (
      <Text>
        {f.shot?.[0] && f.shot[0]?.hit_outcome
          ? HitHelper.getOutcomeLabel(f.shot[0].hit_outcome)
          : '-'}
      </Text>
    ),
  },
  {
    label: '',
    key: ACTIONS_KEY,
    formatFn: (f) => {
      const videoID = getFireVideoID({
        annotated_video_id: f.annotated_video_id,
        replay_video_id: f.replay_video_id,
      });

      return (
        <IconButton variant="soft" size="1" disabled={!videoID}>
          <PlayIcon />
        </IconButton>
      );
    },
    disableSort: true,
  },
];

export const MachineLiveFiresTable = (props: IProps) => {
  const { fires, onSelect } = props;

  const [signingUrls, setSigningUrls] = useState(false);

  const tableSelection: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: onSelect,
  };

  const downloadVideos = async (videoIDs: string[]) => {
    if (videoIDs.length === 0) {
      return;
    }

    setSigningUrls(true);

    const videos = await StereoVideosService.getInstance().getMany(videoIDs);

    const urlDict = await MainService.getInstance().signUrls(
      videos.map((s) => ({ region: s.region, path: s.path }))
    );

    Object.values(urlDict).forEach((entry) => {
      if (entry) {
        window.open(entry.url, '_blank');
      }
    });

    setSigningUrls(false);
  };

  const getCheckedMenuActions = (checked: IFireEvent[]): IMenuAction[] => {
    const videoIDs = ArrayHelper.unique(
      checked.map((s) => (s.replay_video_id ?? s.annotated_video_id) as string)
    );

    const numVideos = videoIDs.length;

    return [
      {
        label:
          numVideos > 1 ? `Download ${numVideos} videos` : 'Download video',
        invisible: !numVideos,
        prefixIcon: <DownloadIcon />,
        disabled: signingUrls || !numVideos,
        onClick: () => downloadVideos(videoIDs),
      },
      {
        label: 'Copy fire IDs',
        prefixIcon: <ClipboardCopyIcon />,
        onClick: () => {
          const ids = checked.map((s) => s._id).join(', ');
          navigator.clipboard.writeText(ids);

          const message_md =
            checked.length > 1
              ? `Copied ${checked.length} fire IDs to clipboard`
              : `Copied ID ${ids} to clipboard`;

          NotifyHelper.success({
            message_md,
          });
        },
      },
    ];
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    checkedActions: getCheckedMenuActions,
  };

  return (
    <Flex
      direction="column"
      gap={RADIX.FLEX.GAP.MD}
      className={RADIX.VFLEX.GROW}
    >
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.FORM}
        table={
          <CommonTableHoC
            dataTestID="MachineLiveFires"
            identifier="MachineLiveFiresTable"
            displayColumns={COLUMNS}
            displayData={fires}
            rowClassNameFn={(f) => {
              if (!f) {
                return '';
              }

              if (f._id === props.updatedFireID) {
                return 'table-updated-row';
              }

              if (f._id === props.newFireID) {
                return 'table-new-row';
              }

              return '';
            }}
            {...tableSelection}
            {...checkable}
            vFlex
          />
        }
      />
    </Flex>
  );
};
